import { useState, useMemo } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Stack,
    Tabs,
    Tab,
    IconButton,
    TextField,
    InputAdornment,
    Tooltip,
    Grid,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import { colors } from "../theme/theme";
import { API_SECTIONS, API_HOST } from "../util/apiSpec";

// ── HTTP method → color ────────────────────────────────────────────────────
const METHOD_COLORS = {
    GET:    { bg: "#0F9D5815", fg: "#0F9D58" },
    POST:   { bg: "#2563EB15", fg: "#2563EB" },
    PATCH:  { bg: "#F59E0B15", fg: "#F59E0B" },
    DELETE: { bg: "#DC262615", fg: "#DC2626" },
    PUT:    { bg: "#7C3AED15", fg: "#7C3AED" },
};

// ── Code block with copy button ────────────────────────────────────────────
const CodeBlock = ({ code, language = "bash" }) => {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // clipboard might be denied in some contexts; silently ignore
        }
    };

    return (
        <Box sx={{
            position: "relative",
            bgcolor: colors.sidebarBg,
            borderRadius: 1.5,
            overflow: "hidden",
            border: `1px solid ${colors.sidebarBorder}`,
        }}>
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5, py: 0.75,
                borderBottom: `1px solid ${colors.sidebarBorder}`,
                bgcolor: colors.sidebarSurface,
            }}>
                <Typography sx={{
                    fontFamily: '"JetBrains Mono"', fontSize: 10,
                    color: colors.sidebarMuted,
                    letterSpacing: "0.12em", textTransform: "uppercase",
                }}>
                    {language}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <IconButton
                        size="small"
                        onClick={copy}
                        sx={{ color: copied ? colors.accent : colors.sidebarMuted, "&:hover": { color: "white" } }}
                    >
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Box component="pre" sx={{
                m: 0, p: 2,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12.5, lineHeight: 1.65,
                color: "#E8E8EE",
                overflowX: "auto",
                whiteSpace: "pre",
                "& .punct": { color: "#8A8A9E" },
                "& .str": { color: "#FFB4A8" },
            }}>
                {code}
            </Box>
        </Box>
    );
};

// ── Single endpoint card ───────────────────────────────────────────────────
const EndpointCard = ({ endpoint, accentColor }) => {
    const [lang, setLang] = useState("curl");
    const methodColor = METHOD_COLORS[endpoint.method] || METHOD_COLORS.GET;

    return (
        <Card
            id={endpoint.id}
            sx={{
                mb: 3,
                scrollMarginTop: 80,
                overflow: "hidden",
                borderColor: colors.border,
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                    <Chip
                        label={endpoint.method}
                        size="small"
                        sx={{
                            bgcolor: methodColor.bg,
                            color: methodColor.fg,
                            fontFamily: '"JetBrains Mono"',
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: "0.08em",
                            height: 24,
                            minWidth: 64,
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: '"JetBrains Mono"',
                            fontSize: 14,
                            color: colors.text,
                            fontWeight: 500,
                            wordBreak: "break-all",
                        }}
                    >
                        {endpoint.path}
                    </Typography>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {endpoint.title}
                </Typography>
                <Typography sx={{ color: colors.textMuted, fontSize: 14, lineHeight: 1.55 }}>
                    {endpoint.description}
                </Typography>
            </Box>

            {/* Params */}
            {endpoint.params && endpoint.params.length > 0 && (
                <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}` }}>
                    <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 1.5 }}>
                        Parameters
                    </Typography>
                    <Box sx={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: 1.5,
                        overflow: "hidden",
                    }}>
                        {endpoint.params.map((p, i) => (
                            <Stack
                                key={i}
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0.5, sm: 2 }}
                                sx={{
                                    p: 1.5,
                                    borderBottom: i < endpoint.params.length - 1 ? `1px solid ${colors.border}` : "none",
                                    "&:hover": { bgcolor: colors.surfaceAlt },
                                }}
                            >
                                <Box sx={{ minWidth: { sm: 180 } }}>
                                    <Typography sx={{
                                        fontFamily: '"JetBrains Mono"',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: colors.text,
                                    }}>
                                        {p.name}
                                        {p.required && <Box component="span" sx={{ color: colors.error, ml: 0.5 }}>*</Box>}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} mt={0.25}>
                                        <Typography sx={{ fontFamily: '"JetBrains Mono"', fontSize: 11, color: colors.textMuted }}>
                                            {p.type}
                                        </Typography>
                                        {p.in && (
                                            <Typography sx={{ fontFamily: '"JetBrains Mono"', fontSize: 11, color: colors.textMuted }}>
                                                · in {p.in}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Typography sx={{ fontSize: 13, color: colors.textMuted, flex: 1 }}>
                                    {p.description || "—"}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Examples (tabbed) */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="overline" sx={{ color: colors.textMuted }}>
                        Example request
                    </Typography>
                </Stack>
                <Tabs
                    value={lang}
                    onChange={(_, v) => setLang(v)}
                    sx={{
                        minHeight: 32,
                        mb: 1.5,
                        "& .MuiTabs-indicator": { backgroundColor: accentColor, height: 2 },
                        "& .MuiTab-root": {
                            minHeight: 32,
                            py: 0.5,
                            px: 1.5,
                            fontFamily: '"JetBrains Mono"',
                            fontSize: 12,
                            textTransform: "lowercase",
                            color: colors.textMuted,
                            "&.Mui-selected": { color: colors.text },
                        },
                    }}
                >
                    <Tab value="curl" label="curl" />
                    <Tab value="javascript" label="javascript" />
                    <Tab value="python" label="python" />
                </Tabs>
                <CodeBlock code={endpoint.examples[lang]} language={lang} />
            </Box>

            {/* Response */}
            <Box sx={{ p: 3 }}>
                <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 1 }}>
                    Response
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Chip
                        label={endpoint.response.contentType}
                        size="small"
                        sx={{
                            bgcolor: colors.surfaceAlt,
                            color: colors.text,
                            fontFamily: '"JetBrains Mono"',
                            fontSize: 11,
                            height: 22,
                        }}
                    />
                    {endpoint.response.description && (
                        <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                            {endpoint.response.description}
                        </Typography>
                    )}
                </Stack>
                {endpoint.response.example && (
                    <CodeBlock code={endpoint.response.example} language="json" />
                )}
            </Box>
        </Card>
    );
};

// ── Sticky left navigation ─────────────────────────────────────────────────
const ApiNav = ({ sections, filter }) => {
    return (
        <Box sx={{
            position: "sticky",
            top: 16,
            pr: 1,
            maxHeight: "calc(100vh - 96px)",
            overflowY: "auto",
        }}>
            {sections.map((section) => (
                <Box key={section.id} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: section.color }} />
                        <Typography
                            sx={{
                                fontFamily: '"JetBrains Mono"',
                                fontSize: 10,
                                letterSpacing: "0.15em",
                                textTransform: "uppercase",
                                color: colors.textMuted,
                                fontWeight: 600,
                            }}
                        >
                            {section.title}
                        </Typography>
                    </Stack>
                    {section.endpoints
                        .filter((ep) => !filter || ep.path.toLowerCase().includes(filter) || ep.title.toLowerCase().includes(filter))
                        .map((ep) => (
                            <Box
                                key={ep.id}
                                component="a"
                                href={`#${ep.id}`}
                                sx={{
                                    display: "block",
                                    py: 0.6, px: 1.5,
                                    borderRadius: 1,
                                    textDecoration: "none",
                                    color: colors.text,
                                    "&:hover": { bgcolor: colors.surfaceAlt },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontFamily: '"JetBrains Mono"',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            color: METHOD_COLORS[ep.method]?.fg || colors.textMuted,
                                            minWidth: 36,
                                        }}
                                    >
                                        {ep.method}
                                    </Box>
                                    <Typography sx={{ fontSize: 13, color: colors.text }}>
                                        {ep.title}
                                    </Typography>
                                </Stack>
                            </Box>
                        ))}
                </Box>
            ))}
        </Box>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────
export const ApiDocsPage = () => {
    const [search, setSearch] = useState("");
    const filter = search.trim().toLowerCase();

    // Filter endpoints by search; hide sections that end up empty
    const filteredSections = useMemo(() => {
        if (!filter) return API_SECTIONS;
        return API_SECTIONS
            .map((s) => ({
                ...s,
                endpoints: s.endpoints.filter((ep) =>
                    ep.path.toLowerCase().includes(filter) ||
                    ep.title.toLowerCase().includes(filter) ||
                    ep.description.toLowerCase().includes(filter)
                ),
            }))
            .filter((s) => s.endpoints.length > 0);
    }, [filter]);

    return (
        <Box>
            {/* Hero */}
            <Box mb={4}>
                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    Developer Documentation
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
                    API Reference.
                </Typography>
                <Typography sx={{ color: colors.textMuted, maxWidth: 720, mb: 3 }}>
                    Every endpoint exposed by KM-BIZZ PDF Tools — PDF processing, the editor, public stats, and the feedback wall.
                    All endpoints are free, no authentication required.
                </Typography>

                {/* Base URL card */}
                <Card sx={{ bgcolor: colors.sidebarBg, color: "white", border: "none", mb: 2 }}>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={2}>
                            <Box>
                                <Typography sx={{
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 10,
                                    letterSpacing: "0.15em",
                                    textTransform: "uppercase",
                                    color: colors.sidebarMuted,
                                }}>
                                    Base URL
                                </Typography>
                                <Typography sx={{
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 15,
                                    fontWeight: 500,
                                    color: "white",
                                }}>
                                    {API_HOST}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    label="NO AUTH"
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(15, 157, 88, 0.2)",
                                        color: "#4ADE80",
                                        fontFamily: '"JetBrains Mono"',
                                        fontWeight: 600,
                                        fontSize: 10,
                                        letterSpacing: "0.1em",
                                        height: 22,
                                    }}
                                />
                                <Chip
                                    label="REST"
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(255, 90, 78, 0.2)",
                                        color: colors.accent,
                                        fontFamily: '"JetBrains Mono"',
                                        fontWeight: 600,
                                        fontSize: 10,
                                        letterSpacing: "0.1em",
                                        height: 22,
                                    }}
                                />
                                <Chip
                                    label="CORS *"
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(37, 99, 235, 0.2)",
                                        color: "#60A5FA",
                                        fontFamily: '"JetBrains Mono"',
                                        fontWeight: 600,
                                        fontSize: 10,
                                        letterSpacing: "0.1em",
                                        height: 22,
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Search */}
                <TextField
                    fullWidth
                    placeholder="Search endpoints — e.g. 'merge' or '/api/stats'"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: colors.textMuted }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Grid container spacing={3}>
                {/* Left nav */}
                <Grid item xs={12} md={3} sx={{ display: { xs: "none", md: "block" } }}>
                    <ApiNav sections={filteredSections} filter={filter} />
                </Grid>

                {/* Content */}
                <Grid item xs={12} md={9}>
                    {filteredSections.length === 0 && (
                        <Card>
                            <CardContent sx={{ p: 4, textAlign: "center" }}>
                                <Typography sx={{ color: colors.textMuted }}>
                                    No endpoints match "{search}".
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {filteredSections.map((section) => (
                        <Box key={section.id} mb={4}>
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: section.color }} />
                                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
                                    {section.title}
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: colors.textMuted, mb: 2.5, maxWidth: 720 }}>
                                {section.description}
                            </Typography>

                            {section.endpoints.map((endpoint) => (
                                <EndpointCard
                                    key={endpoint.id}
                                    endpoint={endpoint}
                                    accentColor={section.color}
                                />
                            ))}
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </Box>
    );
};
